import classnames from "classnames";
import { CSSProperties } from "react";
import Icon from "../Icon";
import LoadingSpinner from "../Loading/LoadingSpinner";

import styles from "./Button.module.css";

export type ButtonType = "button" | "reset" | "submit" | undefined;
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "borderless-secondary"
  | "active"
  | "destructive"
  | "popup"
  | "link"
  | "border-link";
export type ButtonSize = "extraSmall" | "small";

export interface ButtonProps {
  type?: ButtonType;
  form?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  text?: string;
  image?: string;
  title?: string;
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  ariaHasPopup?: boolean;
  ariaExpanded?: boolean;
  iconVariant?: ButtonVariant;
  customStyles?: CSSProperties;
  dataCy?: string;
  ariaLabel?: string;
  tabIndex?: number;
  id?: string;
  dataPrTooltip?: string;
  dataPrMy?: string;
  mobileButton?: boolean;
}

function getIconColor(buttonVariant: string, iconVariant: string) {
  switch (buttonVariant) {
    case "primary":
      return iconVariant === "primary" ? "White" : "Primary-700";
    case "secondary":
    case "borderless-secondary":
      return iconVariant === "primary" ? "buttonSecondary" : "Primary-700";
    case "active":
      return "buttonActive";
    case "destructive":
      return "buttonDestructive";
    case "popup":
    case "link":
      return "buttonPopup";
    case "border-link":
      return "Primary-700";
    default:
      return "buttonPrimary";
  }
}

function getLoadingTheme(variant: string) {
  switch (variant) {
    case "primary":
      return "light";
    case "destructive":
      return "error";
    default:
      return "primary";
  }
}

export const Button = ({
  type = "button",
  form,
  variant = "primary",
  size = "small",
  text,
  image,
  loading = false,
  title,
  className = "",
  disabled = false,
  onClick = undefined,
  onKeyDown,
  ariaHasPopup,
  ariaExpanded,
  iconVariant,
  customStyles,
  dataCy,
  ariaLabel,
  tabIndex = 0,
  id,
  mobileButton,
  dataPrTooltip,
  dataPrMy,
}: ButtonProps) => {
  const iconSize = {
    extraSmall: 20,
    small: 24,
  }[size];

  return (
    <button
      id={id}
      data-cy={dataCy}
      type={type}
      form={form}
      className={classnames([styles.button], {
        [styles.buttonExtraSmall]: size === "extraSmall",
        [styles.buttonSmall]: size === "small",
        [styles.buttonPrimary]: variant === "primary",
        [styles.buttonSecondary]: variant === "secondary",
        [styles.buttonActive]: variant === "active",
        [styles.buttonDestructive]: variant === "destructive",
        [styles.buttonPopup]: variant === "popup",
        [styles.buttonLink]: variant === "link",
        [styles.buttonBorderLink]: variant === "border-link",
        [styles.buttonBorderless]: variant === "borderless-secondary",
        [styles.buttonWithIcon]: mobileButton,
        [className]: !!className,
      })}
      title={title}
      aria-label={ariaLabel ?? text ?? title ?? image}
      aria-haspopup={ariaHasPopup || undefined}
      aria-expanded={ariaHasPopup ? ariaExpanded : undefined}
      tabIndex={tabIndex}
      disabled={disabled || loading}
      onClick={(e) => !loading && onClick?.(e)}
      onKeyDown={onKeyDown}
      style={customStyles || {}}
      data-pr-tooltip={dataPrTooltip}
      data-pr-my={dataPrMy}
    >
      {loading && (
        <div style={{ marginRight: "8px" }}>
          <LoadingSpinner size={20} theme={getLoadingTheme(variant)} />
        </div>
      )}
      {image ? (
        <Icon
          name={image}
          size={iconSize}
          color={getIconColor(variant, iconVariant ?? "primary")}
          className={classnames({
            "m-0": !text,
          })}
        />
      ) : null}
      {text ? <span>{text}</span> : null}
    </button>
  );
};

export default Button;
